<template>
  <v-expansion-panels accordion v-model="showPanel" class="base-shadow">
    <v-expansion-panel expand>
      <v-expansion-panel-header>
        <content-line icon="custom/video-new" text="Share & Control Video" />
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <div class="pa-4">
          <field-block
            class="mb-2"
            title="Share a video and control it's playback."
          >
            <v-select
              label="VideoUrl"
              v-model="context.state.videoUrl"
              :items="videos"
              class="background-dark"
              @change="pause"
            />
          </field-block>
          <video-player
            ref="player"
            :item="videoItem"
            :active="true"
            :popup="false"
            :taking="false"
            :testing="false"
            :options="hostPlayerOptions"
            @currentTime="currentTime"
            @pause="pause"
            @played="play"
            @seek="seek"
          />

          <v-btn class="primary mt-5" @click="close"> Stop & Close </v-btn>
        </div>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import { mapState } from 'vuex';
import throttle from 'lodash/throttle';

import VideoPlayer from '@/views/lessons/lessonWidgets/videoWidget/VideoPlayer';

const getUpdatedAt = () => Math.floor(new Date().getTime() / 1000);
const updatesGapInMs = 100;

export default {
  name: 'ActionVideoControls',
  components: {
    VideoPlayer,
  },
  props: {
    context: {
      type: Object,
      default: () => ({}),
    },
    actionData: {
      type: Object,
      default: () => ({}),
    },
  },
  watch: {
    'context.state': {
      immediate: true,
      handler(state) {
        if (!state) return;
        this.handleReceivedAction(state);
      },
    },
  },
  data() {
    return {
      showPanel: 0,
      playerStatus: '',
      ignoreEvents: false,

      hostPlayerOptions: {
        time: 0,
        qualityControl: false,
        qualityMax: 360, //224, 360, 540, 720, 1080, 3840
      },
    };
  },
  computed: {
    ...mapState('rooms', ['roomInfo']),
    videoItem() {
      return {
        values: {
          url: this.videoUrl,
          summary: {},
        },
      };
    },

    videoUrl() {
      return this.context.state.videoUrl;
    },

    videoName() {
      const video = this.videos.find((v) => v.value === this.videoUrl);
      return video?.text || '';
    },

    videos() {
      const officePattern = this.roomInfo?.on ?? '';
      return this.actionData.videos.filter((v) => {
        return !v?.if || v.if(officePattern);
      });
    },
  },

  methods: {
    currentTime: throttle(function (data) {
      if (!this.isActionOwner()) return;

      this.context.state.seconds = data.seconds;
      this.updateAction('seek');
    }, updatesGapInMs),

    pause(e) {
      this.playerStatus = 'pause';
      this.updateAction('pause', e);
    },

    seek(seconds) {
      if (this.ignoreEvents) return;

      const seekDiff = seconds - this.context.state.seconds;
      if (seekDiff > 5 || seekDiff < -5) {
        this.changeOwner();
        if (!this.isActionOwner()) return;
      }

      this.context.state.seconds = seconds;
      this.updateAction('seek');
    },

    play(e) {
      this.playerStatus = 'play';
      this.updateAction('play', e);
    },

    async updateAction(event, eventDetails) {
      if (!this.isValidAction(eventDetails)) return;
      this.updateContext(event);
      this.$emit('update');
    },

    isValidAction(eventDetails) {
      if (eventDetails?.source === 'user-event') {
        this.changeOwner();
      }
      return this.isActionOwner();
    },

    changeOwner() {
      if (this.isActionOwner()) return;
      this.context.state.hostId = this.roomInfo.identity;
      this.setIgnoreEvents();
    },

    setIgnoreEvents() {
      this.ignoreEvents = true;
      setTimeout(() => {
        this.ignoreEvents = false;
      }, 1000);
    },

    isActionOwner() {
      return this.context.state.hostId === this.roomInfo.identity;
    },

    updateContext(event) {
      const state = {
        action: event,
        show: true,
        playerStatus: this.playerStatus,
        updated_at: getUpdatedAt(),
        friendly_name: this.videoName,
      };

      for (let key in state) {
        this.context.state[key] = state[key];
      }
    },

    close() {
      this.context.state.show = false;
      this.context.state.videoUrl = '';
      this.$emit('close');
    },

    assignData() {
      const { playerStatus, videoUrl, seconds } = this.context.state;
      if (!videoUrl && this.videos.length) {
        this.context.state.videoUrl = this.videos[0].value;
      }

      this.playerStatus = playerStatus;
      this.hostPlayerOptions.time = seconds || 0;
      this.setIgnoreEvents();
    },

    // run another host action
    async handleReceivedAction(contextState) {
      await this.$nextTick();

      if (this.isActionOwner() || this.ignoreEvents) return;

      let action = contextState.action;
      const actionCallbacks = {
        play: (ref) => {
          ref.play();
        },
        seek: (ref) => {
          ref.sync(1, contextState.seconds || 0);
          if (contextState.playerStatus) {
            ref[contextState.playerStatus]();
          }
        },
        pause: (ref) => {
          ref.pause();
        },
      };

      if (!action || !actionCallbacks[action]) {
        return;
      }

      this.callOnRef((ref) => {
        actionCallbacks[action](ref);
      });
    },

    async callOnRef(callback) {
      await this.$nextTick();
      let ref = this.$refs.player;
      if (!ref) return;

      callback(ref);
    },
  },
  mounted() {
    this.assignData();
  },
  beforeDestroy() {
    this.settingsPrepared = false;
    this.hostPlayerOptions.time = 0;
    this.playerStatus = '';
  },
};
</script>
